import React from "react";
import { FormDesignConstants, FormMode } from "./Utilities/FormDesignConstants";
import { FormConfiguration } from "./FormConfiguration";
import { XFormAndLayoutDesignPanel } from "./Utilities/XFormAndLayoutDesignPanel";
import { IAction } from "../CommonUI/IAction";

import { IInjectTopItemType, Topbar } from "../CommonUI/BuilderUI/Topbar";
import { AutoBind } from "../rule-engine-core/Common/AutoBind";
import { ISampleDataProvider } from "../rule-engine-core/Common/ISampleDataProvider";
import { IDesignValidationProvider } from "../rule-engine-core/Common/IDesignValidationProvider";
import { FormContext } from "./Utilities/FormContext";
import { XUserFormTester } from "./Utilities/XUserFormTester";

import { DataDefinition } from "../rule-engine-core/Common/Data/DataDefinition";

import { RulesIcon } from "../CommonUI/Icon/RulesIcon";

import { TabletHorizontalIcon } from "../CommonUI/Icon/TabletHorizontalIcon";
import { TabletVerticalIcon } from "../CommonUI/Icon/TabletVerticalIcon";
import { CellphoneHorizontalIcon } from "../CommonUI/Icon/CellphoneHorizontalIcon";
import { CellphoneVerticalIcon } from "../CommonUI/Icon/CellphoneVerticalIcon";
import { PaperIcon } from "../CommonUI/Icon/PaperIcon";
import { BrushIcon } from "../CommonUI/Icon/BrushIcon";
import { OutlineIcon } from "../CommonUI/Icon/OutlineIcon";
import { NoneIcon } from "../CommonUI/Icon/NoneIcon";
import { Sidebar, SidebarItem } from "../CommonUI/BuilderUI/Sidebar";
import { DataIcon } from "../CommonUI/Icon/DataIcon";

import { ConsentIcon } from "../CommonUI/Icon/ConsentIcon";
import { RuleTableIcon } from "../CommonUI/Icon/RuleTableIcon";
import { TestDataIcon } from "../CommonUI/Icon/TestDataIcon";
import { CodingIcon } from "../CommonUI/Icon/CodingIcon";
import { XFormRulePage } from "./Builder/XFormRulePage";
import { BuilderEditPage } from "../CommonUI/BuilderUI/BuilderEditPage";
import { XExecutableTester } from "../CommonUI/Testing/XExecutableTester";
import { ExecutionData } from "../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { DataBuilder } from "../DataBuilder/DataBuilder";
import { IDesignPanelConfig } from "../rule-engine-core/Common/IDesignPanelConfig";
import { UnitTestConfiguration } from "../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";
import { AppGlueCoreInit } from "../AppGlueCoreInit";

interface XFormDesignerProps {
    form: FormConfiguration;
    formData?: ExecutionData;
    formName?: string;
    dataDefinition?: DataDefinition | (() => DataDefinition);
    onFormClose?: () => void;
    onFormSave?: (data?: object) => void;
    onFormNameUpdate?: (title: string) => void;
    sampleDataProvider?: ISampleDataProvider;
    topDesignerExtensions?: IAction[];
    bottomDesignerExtensions?: IAction[];
    designConfig?: IDesignPanelConfig;
    designValidationProvider?: IDesignValidationProvider;
    initialMode?: FormMode;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    getUnitTests?: () => Promise<UnitTestConfiguration[]>;
}

export class FormBuilder extends React.Component<
    XFormDesignerProps,
    {
        mode: FormMode | string;
        isJsonEditing: boolean;
        nextMode: FormMode | string;
    }
> {
    formName: string = "";
    // this is either designer or runtime form
    editContext: FormContext;

    tabs: SidebarItem[] = [];
    designConfig: IDesignPanelConfig = this.getDefaultDesignConfig();
    runtimeConfig: IDesignPanelConfig = this.getDefaultRuntimeConfig();

    constructor(props: XFormDesignerProps) {
        super(props);

        AppGlueCoreInit.init();

        this.editContext = this.setupProps(props);

        this.state = {
            mode: FormMode.FormDesign,
            isJsonEditing: false,
            nextMode: FormMode.FormDesign,
        };

        if (props.dataDefinition) {
            props.form.inputData = props.dataDefinition;
        }
    }

    componentDidUpdate(prevProps: XFormDesignerProps) {
        if (this.props.form !== prevProps.form) {
            this.editContext = this.setupProps(this.props);

            this.setState({
                mode: FormMode.FormDesign,
                isJsonEditing: false,
                nextMode: FormMode.FormDesign,
            });
        } else {
            if (this.props.formData !== prevProps.formData) {
                this.editContext.setFormData(this.props.formData ? this.props.formData : new ExecutionData());
            }

            if (this.props.formName !== prevProps.formName) this.editContext.formName = this.props.formName;
            if (this.props.designValidationProvider !== prevProps.designValidationProvider)
                this.editContext.designValidationProvider = this.props.designValidationProvider;
            if (this.props.onFormClose !== prevProps.onFormClose) this.editContext.onFormClose = this.props.onFormClose;
            if (this.props.onFormSave !== prevProps.onFormSave) this.editContext.onFormSave = this.props.onFormSave;
            if (this.props.sampleDataProvider !== prevProps.sampleDataProvider)
                this.editContext.sampleDataProvider = this.props.sampleDataProvider;
            if (this.props.topDesignerExtensions !== prevProps.topDesignerExtensions)
                this.editContext.topDesignerExtensions = this.props.topDesignerExtensions;
            if (this.props.bottomDesignerExtensions !== prevProps.bottomDesignerExtensions)
                this.editContext.bottomDesignerExtensions = this.props.bottomDesignerExtensions;
            if (this.props.designConfig !== prevProps.designConfig)
                this.editContext.designConfig = this.props.designConfig;

            if (this.editContext.designConfig?.data === FormDesignConstants.FORM_DATA_MODE_CURRENT) {
                this.editContext.data = new ExecutionData(this.editContext.getFormData().values);
            }

            const { topDesignerExtensions, bottomDesignerExtensions } = this.props;

            if (
                topDesignerExtensions !== prevProps.topDesignerExtensions ||
                bottomDesignerExtensions !== prevProps.bottomDesignerExtensions
            ) {
                this.tabs = this.getDefaultTabs();
                if (topDesignerExtensions) {
                    this.tabs.splice(
                        0,
                        0,
                        ...(topDesignerExtensions || []).map((d) => ({
                            icon: d.icon,
                            label: d.name,
                            value: d.name,
                            renderUI: d.renderUI,
                        })),
                    );
                }
                if (bottomDesignerExtensions) {
                    this.tabs = [
                        ...this.tabs,
                        ...(bottomDesignerExtensions || []).map((d) => ({
                            icon: d.icon,
                            label: d.name,
                            value: d.name,
                            renderUI: d.renderUI,
                        })),
                    ];
                }
            }
        }

        if (this.props.formName !== prevProps.formName) this.formName = this.props.formName || "";

        // set for data definition
        if (this.props.dataDefinition !== prevProps.dataDefinition && this.props.dataDefinition) {
            this.props.form.inputData = this.props.dataDefinition;
        }
    }

    @AutoBind
    setupProps(props: XFormDesignerProps): FormContext {
        let eContext = new FormContext(props.form);
        eContext.designer = this;

        if (props.formData) eContext.setFormData(props.formData);

        eContext.formName = props.formName;
        eContext.designValidationProvider = props.designValidationProvider;
        eContext.onFormClose = props.onFormClose;
        eContext.onFormSave = props.onFormSave;
        eContext.sampleDataProvider = props.sampleDataProvider;
        eContext.topDesignerExtensions = props.topDesignerExtensions;
        eContext.bottomDesignerExtensions = props.bottomDesignerExtensions;
        eContext.designConfig = props.designConfig || this.getDefaultDesignConfig();

        if (eContext.designConfig.data === FormDesignConstants.FORM_DATA_MODE_CURRENT) {
            const data = this.getDefaultData(props.form);
            eContext.data.values = data;
        } else if (eContext.designConfig.data === FormDesignConstants.FORM_DATA_MODE_NONE) {
            eContext.data.values = {};
        }

        if (props.initialMode) {
            eContext.mode = props.initialMode;
        }

        this.editContext = eContext;

        this.props.form.setFormContext(eContext);

        if (props.formName) {
            this.formName = props.formName;
        }

        this.tabs = this.getDefaultTabs();

        const { topDesignerExtensions, bottomDesignerExtensions } = props;
        if (topDesignerExtensions) {
            this.tabs.splice(
                0,
                0,
                ...topDesignerExtensions.map((d) => ({
                    icon: d.icon,
                    label: d.name,
                    value: d.name,
                    renderUI: d.renderUI,
                })),
            );
        }
        if (bottomDesignerExtensions) {
            this.tabs = [
                ...this.tabs,
                ...bottomDesignerExtensions.map((d) => ({
                    icon: d.icon,
                    label: d.name,
                    value: d.name,
                    renderUI: d.renderUI,
                })),
            ];
        }

        return eContext;
    }

    getDefaultTabs(): SidebarItem[] {
        return [
            {
                label: "Layout",
                icon: <RuleTableIcon />,
                value: FormMode.LayoutDesign,
                renderUI: () => <XFormAndLayoutDesignPanel editContext={this.editContext!} />,
            },
            {
                label: "Form",
                icon: <ConsentIcon />,
                value: FormMode.FormDesign,
                renderUI: () => <XFormAndLayoutDesignPanel editContext={this.editContext!} />,
            },
            {
                label: "Run",
                icon: <TestDataIcon />,
                value: FormMode.Runtime,
                renderUI: () => <XUserFormTester editContext={this.editContext} />,
            },

            {
                label: "Test",
                icon: <TestDataIcon />,
                value: FormMode.Test,
                renderUI: () => (
                    <XExecutableTester
                        saveUnitTest={this.props.saveUnitTest}
                        builderName={"Test"}
                        executable={this.props.form}
                    />
                ),
            },
            {
                label: "Config",
                icon: <CodingIcon />,
                value: FormMode.JSON,
                renderUI: () => (
                    <BuilderEditPage
                        builder={this.props.form}
                        updateCallback={() => {}}
                    />
                ),
            },
            {
                label: "Data",
                icon: <DataIcon />,
                value: FormMode.Data,
                renderUI: () => (
                    <DataBuilder
                        definition={this.props.form.getInputDefinition()}
                        readOnly={!this.props.form.canChangeInputs}
                    />
                ),
            },
            {
                label: "Rules",
                icon: <RulesIcon />,
                value: FormMode.Rules,
                renderUI: () => <XFormRulePage form={this.props.form} />,
            },
        ];
    }

    // form events.
    @AutoBind
    onFormSave() {
        // Save Action
        this.editContext.onFormSave!(this.editContext.getFormData());

        // form Name update
        if (this.editContext.formName) {
            this.formName = this.editContext.formName;
            if (this.props.onFormNameUpdate) {
                this.props.onFormNameUpdate(this.editContext.formName);
            }
        }
    }

    @AutoBind
    onFormClose() {
        // Cancel Action
        this.editContext.onClose();

        // reset form name on cancel action
        this.formName = this.editContext.formName = this.props.formName || "";
    }

    @AutoBind
    onCopy() {}

    @AutoBind
    onCut() {}

    @AutoBind
    onDelete() {}

    getDefaultDesignConfig(): IDesignPanelConfig {
        return {
            data: FormDesignConstants.FORM_DATA_MODE_CURRENT,
            mode: FormDesignConstants.FORM_MODE_PAPER,
        };
    }

    getDefaultRuntimeConfig(): IDesignPanelConfig {
        return {
            data: FormDesignConstants.FORM_DATA_MODE_CURRENT,
            mode: FormDesignConstants.FORM_MODE_PAPER,
        };
    }

    getDefaultData(form: FormConfiguration) {
        let dataDef = form.getInputDefinition();

        const data = dataDef.getValueObject() || {};
        return data;
    }

    @AutoBind
    handleModeChange(mode: string) {
        if (this.state.mode === FormMode.Runtime) {
            this.editContext.designConfig = this.runtimeConfig;
        } else {
            this.editContext.designConfig = this.designConfig;
        }

        if (this.state.mode !== FormMode.JSON || !this.state.isJsonEditing) {
            this.editContext.mode = mode;
            this.setState({ mode, nextMode: mode });
        } else {
            this.setState({ nextMode: mode });
        }
    }

    onJsonEditing = (isJsonEditing: boolean) => {
        this.setState({
            isJsonEditing,
        });
    };

    onNextMode = (isLeaving: boolean) => {
        if (isLeaving) {
            this.editContext.mode = this.state.nextMode;
            this.setState({
                mode: this.state.nextMode,
            });
        } else {
            this.setState({
                nextMode: this.state.mode,
            });
        }
    };

    @AutoBind
    onChangeSampleData(value: string) {
        let des = this.editContext.designConfig;
        if (des) {
            des.data = value;
            if (value === FormDesignConstants.FORM_DATA_MODE_NONE) {
                this.editContext.data.values = {};
            } else if (value === FormDesignConstants.FORM_DATA_MODE_CURRENT) {
                const data = this.getDefaultData(this.props.form);
                this.editContext.data.values = {
                    ...data,
                };
            }

            this.forceUpdate();
        }
    }

    @AutoBind
    onUpdateDisplayMode(value: string) {
        let des = this.editContext.designConfig?.mode;
        if (des !== value && this.editContext.designConfig) {
            Reflect.set(this.editContext.designConfig, "mode", value);
            this.forceUpdate();
        }
    }

    @AutoBind
    onChangeFormName(newTitle: string) {
        let des = this.editContext;
        if (des) {
            des.formName = newTitle;
            if (this.editContext.refreshDesigner) this.editContext.refreshDesigner();
        }

        if (this.props.onFormNameUpdate) {
            this.props.onFormNameUpdate(newTitle);
        }
    }

    render() {
        return (
            <div
                style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
                data-testid="FormDesigner"
            >
                <Topbar
                    name={this.editContext.formName}
                    onChangeName={this.onChangeFormName}
                    editContext={this.editContext}
                    isActionDisabled={
                        !(
                            this.editContext.mode === FormMode.FormDesign ||
                            this.editContext.mode === FormMode.LayoutDesign
                        ) || !this.editContext.lastSelectionElement
                    }
                    isRequiredActionGroup={this.editContext.mode !== FormMode.Runtime}
                    onSave={this.props.onFormSave}
                    onClose={this.props.onFormClose}
                    prevItems={[
                        {
                            label: "Show Data",
                            items: FormDesignConstants.FORM_DATA_MODE.map((m) => ({
                                value: m,
                            })),
                            value: this.editContext.designConfig?.data || "",
                            onSelect: this.onChangeSampleData,
                            type: IInjectTopItemType.SELECT,
                        },
                        {
                            label: "Display Format",
                            value: this.editContext.designConfig?.mode || "",
                            items: [
                                {
                                    label: "Paper",
                                    icon: <PaperIcon alt={FormDesignConstants.FORM_MODE_PAPER} />,
                                },
                                {
                                    label: "Gray",
                                    icon: <BrushIcon alt={FormDesignConstants.FORM_MODE_GRAY} />,
                                },
                                {
                                    label: "Outline",
                                    icon: <OutlineIcon alt={FormDesignConstants.FORM_MODE_OUTLINE} />,
                                },
                                {
                                    label: "White",
                                    icon: <NoneIcon alt={FormDesignConstants.FORM_MODE_WHITE} />,
                                },
                                {
                                    label: FormDesignConstants.FORM_MODE_TABLET_HORIZONTAL,
                                    icon: (
                                        <TabletHorizontalIcon alt={FormDesignConstants.FORM_MODE_TABLET_HORIZONTAL} />
                                    ),
                                },
                                {
                                    label: FormDesignConstants.FORM_MODE_TABLET_VERTICAL,
                                    icon: <TabletVerticalIcon alt={FormDesignConstants.FORM_MODE_TABLET_VERTICAL} />,
                                },
                                {
                                    label: FormDesignConstants.FORM_MODE_PHONE_HORIZONTAL,
                                    icon: (
                                        <CellphoneHorizontalIcon alt={FormDesignConstants.FORM_MODE_PHONE_HORIZONTAL} />
                                    ),
                                },
                                {
                                    label: FormDesignConstants.FORM_MODE_PHONE_VERTICAL,
                                    icon: <CellphoneVerticalIcon alt={FormDesignConstants.FORM_MODE_PHONE_VERTICAL} />,
                                },
                            ],
                            onSelect: this.onUpdateDisplayMode,
                            type: IInjectTopItemType.ICONBUTTONGROUP,
                            wrapperStyle: { marginRight: this.editContext.mode === FormMode.Runtime ? "auto" : 0 },
                        },
                    ]}
                />
                <Sidebar
                    items={this.tabs}
                    value={this.editContext.mode}
                    onChange={this.handleModeChange}
                />
            </div>
        );
    }
}
